import type { QueryResult } from '@apollo/client';
import { useQuery } from '@apollo/client';
import type { VariablesOf } from '@graphql-typed-document-node/core';
import _ from 'lodash';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import useIsLoggedIn from '../account/useIsLoggedIn';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { UserType } from '../graphql/generated/graphql';
import updateSubscribedList from '../utils/graphql/updateSubscribedList';

// eslint-disable-next-line slab-graphql/no-unused-colocated-fragments
gql(`
  # eslint-disable-next-line slab-graphql/no-unused-fields
  fragment useAllUsers_user on User {
    id
    name
    email
    title
    description
    type
    deactivatedAt
    ...UserAvatar
  }
`);

export const ALL_USERS_QUERY = gql(`
  query allUsers($includeDeactivated: Boolean) {
    session: currentSession {
      organization {
        id
        users(includeDeactivated: $includeDeactivated) {
          id
          ...useAllUsers_user
        }
      }
    }
  }
`);

const USER_CREATED_SUBSCRIPTION = gql(`
  subscription userCreated {
    user: userCreated {
      ...useAllUsers_user
    }
  }
`);

const USER_UPDATED_SUBSCRIPTION = gql(`
  subscription userUpdated {
    user: userUpdated {
      ...useAllUsers_user
    }
  }
`);

const useSubscribeEffect = (
  subscribeToMore: QueryResult<
    DocumentType<typeof ALL_USERS_QUERY>,
    VariablesOf<typeof ALL_USERS_QUERY>
  >['subscribeToMore'],
  skip: boolean,
) => {
  useEffect(() => {
    if (skip) return undefined;

    const unsubscribes = [
      USER_CREATED_SUBSCRIPTION,
      USER_UPDATED_SUBSCRIPTION,
    ].map((query) => {
      const path = 'session.organization.users';
      return subscribeToMore({
        document: query,
        updateQuery: updateSubscribedList(path, 'user', true),
      });
    });

    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [skip, subscribeToMore]);
};

interface UseAllUsersQueryOptions {
  skip?: boolean;
}

const useAllUsersQuery = (options?: UseAllUsersQueryOptions) => {
  const response = useQuery(ALL_USERS_QUERY, {
    variables: {
      includeDeactivated: true,
    },
    skip: options?.skip,
  });
  const { data } = response;

  useSubscribeEffect(response.subscribeToMore, options?.skip ?? false);

  return useMemo(() => _.compact(data?.session?.organization?.users), [data]);
};

const AllUsersContext = createContext<{
  users: ReturnType<typeof useAllUsersQuery>;
  query: () => void;
}>({ users: [], query: () => {} });

interface UseAllUsersOptions extends UseAllUsersQueryOptions {
  includeDeactivated?: boolean;
}

interface AllUsersProviderProps {
  children: React.ReactNode;
}

export const AllUsersProvider = ({ children }: AllUsersProviderProps) => {
  const [isIdle, setIsIdle] = useState(true);

  const users = useAllUsersQuery({ skip: isIdle });

  const isLoggedIn = useIsLoggedIn();

  const value = useMemo(
    () => ({ query: () => setIsIdle(!isLoggedIn), users }),
    [isLoggedIn, users],
  );

  return (
    <AllUsersContext.Provider value={value}>
      {children}
    </AllUsersContext.Provider>
  );
};

const useAllUsers = (options?: UseAllUsersOptions) => {
  const { users, query } = useContext(AllUsersContext);

  useEffect(() => {
    if (!options?.skip) query();
  }, [options?.skip, query]);

  return useMemo(
    () =>
      options?.includeDeactivated
        ? users
        : users.filter((user) => !user.deactivatedAt),
    [options?.includeDeactivated, users],
  );
};

export const useAllAdmins = (...args: Parameters<typeof useAllUsers>) => {
  const allUsers = useAllUsers(...args);

  return allUsers.filter((user) => user.type === UserType.Admin);
};

export const useAllBots = (...args: Parameters<typeof useAllUsers>) => {
  const allUsers = useAllUsers(...args);

  return allUsers.filter((user) => user.type === UserType.Bot);
};

export const useAllUsersMap = (...args: Parameters<typeof useAllUsers>) => {
  const allUsers = useAllUsers(...args);
  const map = useMemo(() => _.keyBy(allUsers, 'id'), [allUsers]);

  return map;
};

export default useAllUsers;
